import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowRight, ArrowLeft } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { getPhotoUrl } from '../api';
import SheepIcon from '../components/SheepIcon';

export default function CartPage() {
  const { t, lang, cart, removeFromCart, formatPrice, cartTotal, currentCountry } = useApp();
  const navigate  = useNavigate();
  const BackIcon  = lang === 'ar' ? ArrowLeft : ArrowRight;
  const canBuy    = currentCountry?.buying_enabled;
  const isRtl     = lang === 'ar';

  /* ── Empty state ── */
  if (cart.length === 0) return (
    <div className="empty-state" style={{ minHeight: '80vh', paddingBottom: 100 }}>
      <ShoppingBag size={60} color="var(--text-3)" />
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-2)' }}>
        {t.cartEmpty}
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 260, lineHeight: 1.5 }}>
        {t.cartEmptyDesc}
      </div>
      <button className="btn btn-primary" onClick={() => navigate('/')} style={{ marginTop: 4 }}>
        {t.continueShopping}
      </button>
    </div>
  );

  return (
    <div className="page-pb page-center fade-up">

      {/* ── Header ── */}
      <div
        style={{
          padding: '14px 16px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          aria-label={lang === 'ar' ? 'رجوع' : 'Back'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--brand)',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: 'inherit',
            padding: '4px 0',
          }}
        >
          <BackIcon size={17} />
          {lang === 'ar' ? 'رجوع' : 'Back'}
        </button>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-1)' }}>
          {t.cart}
          <span
            style={{
              marginInlineStart: 8,
              background: 'var(--brand)',
              color: '#fff',
              borderRadius: 'var(--r-full)',
              fontSize: 12,
              fontWeight: 700,
              padding: '1px 8px',
            }}
          >
            {cart.length}
          </span>
        </h1>
      </div>

      {/* ── Cart items ── */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {cart.map(sheep => {
          const breedLabel = lang === 'ar' ? sheep.breed_ar : sheep.breed_en;
          const photo      = getPhotoUrl(sheep.photo_url);
          return (
            <div
              key={sheep.id}
              className="card fade-up"
              style={{
                padding: 14,
                display: 'flex',
                gap: 13,
                alignItems: 'center',
              }}
            >
              {/* Thumbnail */}
              <div
                onClick={() => navigate(`/sheep/${sheep.id}`)}
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 'var(--r-md)',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #F5F0EC, #EDE8E3)',
                  flexShrink: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid var(--border)',
                }}
              >
                {photo ? (
                  <img
                    src={photo}
                    alt={breedLabel}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    loading="lazy"
                  />
                ) : (
                  <SheepIcon size={36} color="#7A1010" opacity={0.22} />
                )}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: 'var(--text-1)',
                    marginBottom: 5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {breedLabel || sheep.given_name}
                </div>
                <span className="price-tag" style={{ fontSize: 13 }}>
                  {formatPrice(sheep.online_price)}
                </span>
              </div>

              {/* Remove */}
              <button
                onClick={() => removeFromCart(sheep.id)}
                aria-label={lang === 'ar' ? 'حذف' : 'Remove'}
                style={{
                  background: 'var(--danger-bg)',
                  border: 'none',
                  borderRadius: 'var(--r-sm)',
                  padding: '8px',
                  cursor: 'pointer',
                  color: 'var(--danger)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background var(--ease)',
                  flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#FECACA'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--danger-bg)'; }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Total & Checkout ── */}
      <div
        style={{
          background: 'var(--surface)',
          borderTop: '1px solid var(--border)',
          padding: '16px 16px',
          marginTop: 4,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.07)',
          position: 'sticky',
          bottom: 0,
        }}
      >
        {/* Total row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-2)' }}>
            {t.total}
          </span>
          <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--success)' }}>
            {formatPrice(cartTotal)}
          </span>
        </div>

        {canBuy ? (
          <button
            className="btn btn-primary"
            onClick={() => navigate('/checkout')}
            style={{ width: '100%', padding: '14px 0', fontSize: 16, borderRadius: 'var(--r-lg)' }}
          >
            {t.checkout} ({cart.length})
          </button>
        ) : (
          <div
            style={{
              textAlign: 'center',
              background: 'var(--warn-bg)',
              borderRadius: 'var(--r-md)',
              padding: '12px 16px',
              border: '1px solid #FCD34D',
            }}
          >
            <div style={{ fontSize: 13, color: 'var(--warn-txt)', fontWeight: 500 }}>
              {t.buyingNotAvailable}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
